import { selectorFamily, useSetRecoilState } from "recoil";
import { toast } from "react-hot-toast";

import CollaboratorsAPI from "../api/collaborators.api";
import { bulkLoadAtom, collaboratorsAtom, collaboratorTestsAtom, crudCollaboratorsAtom, informAtom, invitationAtom, templateAtom, iaSummaryAtom } from "../atoms/collaborators.atoms";

const useCollaboratorsActions = () => {
  const setInform = useSetRecoilState(informAtom);
  const setBulkLoad = useSetRecoilState(bulkLoadAtom);
  const setInvitation = useSetRecoilState(invitationAtom);
  const setIaSummary = useSetRecoilState(iaSummaryAtom);
  const setCollaborators = useSetRecoilState(collaboratorsAtom);
  const setCollaboratorTests = useSetRecoilState(collaboratorTestsAtom);
  const setCollaboratorsCrud = useSetRecoilState(crudCollaboratorsAtom);
  const setTemplate = useSetRecoilState(templateAtom)

  const downloadReports = async ({ collaboratorsIds = [] }, cb) => {
    try {
      const params = { collaboratorsIds };
      const { data } = await CollaboratorsAPI.downloadReports(params);
      if (cb) {
        cb(data);
      }
    } catch (error) {
      toast.error('Error al descargar informes');
    }
  };

  const sendReports = async ({ clientId }, cb) => {
    try {
      const params = { clientId };
      const { data } = await CollaboratorsAPI.sendReports(params);
      if (cb) {
        cb(data);
      }
    } catch (error) {
      toast.error('Error al enviar informes');
    }
  };

  const collaboratorsList = async ({ page = 1, limit = 10, search = null, clientId = '', sort = null }, cb) => {
    setCollaborators((prevValues) => ({ ...prevValues, loading: true }));
    try {
      let params = null;
      params = {
        clientId,
        paginationParams: { page, limit }
      };
      if (search) params.paginationParams.search = [search];
      if (sort) params.paginationParams.sort = sort;
      const { data } = await CollaboratorsAPI.collaboratorsList(params);
      if (cb) {
        cb(data);
      }
      setCollaborators((prevValues) => ({ ...prevValues, loading: false, payload: data?.data || [], total: data?.total || 0 }));
    } catch (error) {
      if(error?.response.data.status===200){
        setCollaborators((prevValues) => ({ ...prevValues, loading: false, payload: error?.response.data?.data || [], total: error?.response.data.status.total || 0 }));
      }
      setCollaborators((prevValues) => ({ ...prevValues, loading: false, error }));
    }
  };

  const collaboratorTests = async (id, cb) => {
    setCollaboratorTests((prevValues) => ({ ...prevValues, loading: true }));
    try {
      let params = null;
      params = {
        collaboratorID: id,
      };
      const { data } = await CollaboratorsAPI.collaboratorTests(params);
      if (cb) {
        cb(data);
      }
      setCollaboratorTests((prevValues) => ({ ...prevValues, loading: false, payload: data }));
    } catch (error) {
      setCollaboratorTests((prevValues) => ({ ...prevValues, loading: false, error }));
    }
  };

  const updateCollaborator = async (data, cb) => {
    setCollaboratorsCrud((prevValues) => ({ ...prevValues, loading: true }));
    try {
      const response = await CollaboratorsAPI.updateCollaborator(data, data.id);
      if (cb) {
        cb(response);
      }
      setCollaboratorsCrud((prevValues) => ({ ...prevValues, loading: false, status: response.status }));
    } catch (error) {
      const typeError = error.response?.data?.message
      if(typeError?.includes('(rut)')){
        toast.error('El rut ingresado ya se encuentra registrado, por favor, intenta con otro.', {duration:4000});
      } else if(typeError?.includes('(email)')){
        toast.error('El email ingresado ya se encuentra registrado, por favor, intenta con otro.', {duration:4000});
      } else {
        toast.error('Error al actualizar colaborador.', {duration:4000});
      }
      setCollaboratorsCrud((prevValues) => ({
        ...prevValues,
        loading: false,
        message: 'Error al actualizar colaborador',
        status: 500,
        error: JSON.stringify(error),
      }));
    }
  };

  const createCollaborator = async (data, cb) => {
    setCollaboratorsCrud((prevValues) => ({ ...prevValues, loading: true }));
    try {
      const response = await CollaboratorsAPI.createCollaborator(data);
      if (cb) {
        cb(response);
      }
      setCollaboratorsCrud((prevValues) => ({ ...prevValues, loading: false, status: response.status }));
    } catch (error) {
      const typeError = error.response?.data?.message
      if(typeError?.includes('(rut)')){
        toast.error('El rut ingresado ya se encuentra registrado, por favor, intenta con otro.', {duration:4000});
      } else if(typeError?.includes('(email)')){
        toast.error('El email ingresado ya se encuentra registrado, por favor, intenta con otro.', {duration:4000});
      } else {
        toast.error('Error al crear colaborador.', {duration:4000});
      }
      setCollaboratorsCrud((prevValues) => ({
        ...prevValues,
        loading: false,
        message: 'Error al crear colaborador',
        status: 500,
        error: JSON.stringify(error),
      }));
    }
  };

  const deleteCollaborator = async (id, cb)=>{
    setCollaboratorsCrud((prevValues) => ({ ...prevValues, loading: true }));
    try {
      const response = await CollaboratorsAPI.deleteCollaborator({id});
      if(cb){
        cb(response)
      }
      setCollaboratorsCrud((prevValues) => ({ ...prevValues, loading: false, status: response.status })); 
    } catch (error) {
      toast.error(error?.request?.responseText || 'Error al eliminar colaborador');
      setCollaboratorsCrud((prevValues) => ({
        ...prevValues,
        loading: false,
        message: 'Error al borrar colaborador',
        status: 500,
        error: JSON.stringify(error),
      }));
    }
    }

  const sendInvitation = async (body, cb) => {
    setInvitation((prevValues) => ({ ...prevValues, loading: true }));
    try {
      const { data } = await CollaboratorsAPI.sendInvitation(body);
      if (cb) {
        cb(data);
      }
      setInvitation((prevValues) => ({ ...prevValues, loading: false, payload: data }));
    } catch (error) {
      if (cb) {
        let errorControl = error.response.status === 401 ? 401 : 500;
        cb(errorControl);
      }
      setInvitation((prevValues) => ({ ...prevValues, loading: false, error, message: 'Error al enviar invitación' }));
    }
  };

  const buildIaSummary = async (body, cb) => {
    setIaSummary((prevValues) => ({ ...prevValues, loading: true }));
    try {
      const { data } = await CollaboratorsAPI.buildIaSummary(body);
      if (cb) {
        cb(data);
      }
      setIaSummary((prevValues) => ({ ...prevValues, loading: false, payload: data }));
    } catch (error) {
      if (cb) {
        let errorControl = error.response.status === 401 ? 401 : 500;
        cb(errorControl);
      }
      setIaSummary((prevValues) => ({ ...prevValues, loading: false, error, message: 'Error al generar resumen IA' }));
    }
  };

  const resendInvitation = async (id, cb) => {
    setInvitation((prevValues) => ({ ...prevValues, loading: true }));
    try {
      const { data } = await CollaboratorsAPI.resendInvitation({ "collaboratorTestIDs": [`${id}`] });
      if (cb) {
        cb(data);
      }
      setInvitation((prevValues) => ({ ...prevValues, loading: false, payload: data }));
    } catch (error) {
      if (cb) {
        cb(500);
      }
      setInvitation((prevValues) => ({ ...prevValues, loading: false, error, message: 'Error al enviar invitación' }));
    }
  };

  const bulkLoad = async (data, cb) => {
    setBulkLoad((prevValues) => ({ ...prevValues, loading: true }));
    try {
      const response = await CollaboratorsAPI.bulkLoad(data);
      if (cb) {
        cb(response);
      }
      setBulkLoad((prevValues) => ({ ...prevValues, loading: false, status: response.status }));
    } catch (error) {
      if (cb) cb(error);
      setBulkLoad((prevValues) => ({
        ...prevValues,
        loading: false,
        message: error?.request?.responseText || 'Error al cargar colaboradores',
        status: 500,
        error
      }));
    }
  };

  const donwloadInform = async ({ id, extension }, cb) => {
    setInform((prevValues) => ({ ...prevValues, loading: true }));
    try {
      const response = await CollaboratorsAPI.donwloadInform(id, extension);
      if (cb) {
        cb(response);
      }
      setInform((prevValues) => ({ ...prevValues, loading: false, status: response.status }));
    } catch (error) {
      setInform((prevValues) => ({
        ...prevValues,
        loading: false,
        message: 'Error al descargar el informe',
        status: 500,
        error
      }));
    }
  };

  const templateExcel = async(cb)=>{
    setTemplate((prevValues)=>({...prevValues, loading:true}))
    try {
      const response =await CollaboratorsAPI.getExcelTemplate();
      return response;
    } catch (error) {
      setTemplate((prevValues)=>({
        ...prevValues, 
        loading:true,
        message:'Error al descargar la plantilla',
        status:500,
        error
      }))
    }
  }
  const listCollaborator =async ({ page = 1, limit = 100000, search = null, clientId = '' }, cb) => {
    setCollaborators((prevValues) => ({ ...prevValues, loading: true }));
    try {
      let params = null;
      params = {
        clientId,
        paginationParams: { page, limit }
      };
      if (search) params.paginationParams.search = [search];
      const { data } = await CollaboratorsAPI.collaboratorsList(params);
      if(cb){
        cb(data)
      }
      setCollaborators((prevValues) => ({ ...prevValues, loading: false }));
      return data
    } catch (error) {
      setCollaborators((prevValues) => ({ ...prevValues, loading: false }));
      return {data: []}
    }
  }
  const resetInform = () => setInform({ message: '', loading: false, payload: null });
  const resetBulkLoad = () => setBulkLoad({ status: 500, message: '', loading: false });
  const resetInvitation = () => setInvitation({ message: '', loading: false, payload: null });
  const resetCollaboratorsList = () => setCollaborators({ payload: [], loading: false });
  const resetCollaboratorCrud = () => setCollaboratorsCrud({ status: 500, message: '', loading: false });

  return {
    bulkLoad,
    donwloadInform,
    sendInvitation,
    buildIaSummary,
    resendInvitation,
    downloadReports,
    sendReports,
    collaboratorsList,
    collaboratorTests,
    updateCollaborator,
    createCollaborator,
    resetInform,
    resetBulkLoad,
    resetInvitation,
    resetCollaboratorCrud,
    resetCollaboratorsList,
    templateExcel,
    deleteCollaborator,
    listCollaborator
  };
}

const collaboratorTestsSelector = selectorFamily({
  key: 'collaboratorTests',
  get: (id) => async () => {
    let params = null;
    params = {
      collaboratorID: id,
    };
    const response = await CollaboratorsAPI.collaboratorTests(params);
    if (response.error) {
      throw response.error;
    }
    return response?.data || [];
  },
});

export { useCollaboratorsActions, collaboratorTestsSelector };